<script setup lang="ts">
interface Props {
  fixedContent?: boolean
  collapsibleAside?: boolean
  isLoading?: boolean
  contentBottom?: number
  headerHeight?: number
  contentTop?: number
  asideWidth?: 'wide' | 'narrow'
  bannerHeight?: number
  /** Controls the licensed/unlicensed background styling on the content column. Defaults to true (white background). */
  licensedContent?: boolean
  /** Controls the licensed/unlicensed background styling on the aside column. Defaults to true (white background). */
  licensedAside?: boolean
  context?: 'law' | 'literature' | 'journal' | 'academic_article'
  activeView?: ReadingView
  readingTextLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  fixedContent: false,
  collapsibleAside: false,
  isLoading: false,
  contentBottom: 0,
  headerHeight: 0,
  contentTop: 0,
  bannerHeight: 0,
  asideWidth: 'wide',
  licensedContent: true,
  licensedAside: true,
  context: undefined,
  activeView: 'reading',
  readingTextLabel: undefined,
})

const { loggedIn } = useUserSession()

useReadingModeSync()

const sidebarExpanded = defineModel('sidebarExpanded', { default: true })
const contentExpanded = defineModel('contentExpanded', { default: true })
const commentExpanded = ref<boolean>(true)

const contentRef = useTemplateRef('contentContainer')
const asideContainerRef = useTemplateRef('asideContainer')
const referenceContainerRef = useTemplateRef('referenceContainer')
const tocContainerRef = useTemplateRef('tocContainer')

const { arrivedState } = useScroll(contentRef)

const columnHeight = computed<string>(() => `calc(100dvh - ${props.contentBottom}px)`)
// Subtract the top gap so the page does not overflow and drag on iOS
const mobileColumnHeight = computed<string>(() => `calc(100svh - ${props.contentBottom}px - 1rem)`)

const displayControlButton = computed(() => {
  return !loggedIn.value || props.collapsibleAside
})

onMounted(() => {
  document.body.style.overflow = 'hidden'
})

const transitionsReady = ref<boolean>(false)
onMounted(() => {
  requestAnimationFrame(() => {
    transitionsReady.value = true
  })
})

// Classic scrollbars take their width out of the scroll containers' right padding, overlay scrollbars take none
// The probe copies the containers' stable gutter so both agree, headless Chromium included
const scrollbarWidth = ref<number>(0)
function measureScrollbarWidth() {
  if (!CSS.supports('scrollbar-gutter', 'stable')) {
    return
  }
  const probe = document.createElement('div')
  probe.className = 'scrollbar-carbon'
  probe.style.overflowY = 'scroll'
  probe.style.scrollbarGutter = 'stable'
  document.body.appendChild(probe)
  scrollbarWidth.value = probe.offsetWidth - probe.clientWidth
  probe.remove()
}
onMounted(measureScrollbarWidth)
// Page zoom fires resize, and the scrollbar keeps its device-pixel width, so its CSS width changes
useEventListener('resize', measureScrollbarWidth)

onUnmounted(() => {
  document.body.style.overflow = ''
})

watch(() => props.collapsibleAside, (value) => {
  if (!value) {
    commentExpanded.value = true
  }
})

watch(contentExpanded, (expanded) => {
  if (expanded) {
    arrivedState.top = true
  }
})

const containers = {
  content: contentRef,
  aside: asideContainerRef,
  toc: tocContainerRef,
}

provide(diKeys.containers, containers)
useMarkingsRehydration(containers)

const viewAnnouncement = ref<string>('')
// Skip the first render animation. Enable it after the first view switch
const viewSwitched = ref<boolean>(false)

const READING_TEXT_ANNOUNCEMENTS: Partial<Record<NonNullable<Props['context']>, string>> = {
  law: 'Lovtekst',
  literature: 'Boktekst',
  academic_article: 'Artikkeltekst',
  journal: 'Artikkeltekst',
}

const readingTextLabel = computed(() => {
  if (props.readingTextLabel) {
    return props.readingTextLabel
  }

  return (props.context && READING_TEXT_ANNOUNCEMENTS[props.context]) ?? 'Tekst'
})

const readingTextLabelLower = computed(() => readingTextLabel.value.toLowerCase())

const viewAnnouncements = computed<Record<ReadingView, string>>(() => ({
  reading: readingTextLabel.value,
  toc: 'Innhold',
  commentary: 'Lovkommentar åpnet',
  curated: 'Utvalgte kilder',
  literature: 'Relatert litteratur',
  'literature-list': 'Litteraturliste',
  reference: props.context === 'law' ? 'Kilde' : 'Kilder',
}))

watch(() => props.activeView, (view) => {
  viewSwitched.value = true
  if (!props.context || !import.meta.client || !window.matchMedia('(max-width: 1349px)').matches) {
    return
  }
  viewAnnouncement.value = viewAnnouncements.value[view]
  nextTick(() => {
    // Move focus without scrolling. This keeps keyboard/SR support and avoids pushing the toolbar off-screen
    if (view === 'reference') {
      // The article has no reference column, its reference view lives in the aside
      (referenceContainerRef.value ?? asideContainerRef.value)?.focus({ preventScroll: true })
    }
    else if (view === 'toc') {
      tocContainerRef.value?.focus({ preventScroll: true })
    }
    else if (view !== 'reading') {
      asideContainerRef.value?.focus({ preventScroll: true })
    }
  })
})
</script>

<template>
  <div v-if="$slots.sidebar || $slots.content || $slots.aside || $slots.reference" :class="[readingVariants.layout({ fixedContent }), context && 'max-md:px-0 max-md:h-auto']" :style="{ '--scrollbar-width': `${scrollbarWidth}px` }">
    <div v-if="context" class="sr-only" aria-live="polite" role="status">
      {{ viewAnnouncement }}
    </div>
    <div class="flex gap-3xs w-full" :data-reading-tablet="context ? '' : undefined" :data-context="context" :data-active-view="context ? activeView : undefined" :data-views-ready="viewSwitched ? '' : undefined" :data-transitions-off="transitionsReady ? undefined : ''">
      <div v-if="$slots.sidebar" class="relative" data-sidebar-wrapper>
        <TableOfContentCollapseButton
          v-if="!isLoading"
          v-model:expanded="sidebarExpanded"
          direction="left"
          expanded-text="Lukk sidepanel"
          collapsed-text="Åpne sidepanel"
          class="absolute top-[1.13rem] right-0 z-10"
          data-sidebar-toggle
        />
        <div data-column data-view="toc" :data-sidebar-expanded="sidebarExpanded ? '' : undefined" :class="readingVariants.sidebar({ expanded: sidebarExpanded })">
          <div v-if="sidebarExpanded" ref="tocContainer" tabindex="-1" class="scrollbar-carbon overflow-y-auto [scrollbar-gutter:stable] pr-[calc(theme(spacing.m)-var(--scrollbar-width))] pb-l">
            <slot name="sidebar" />
          </div>
          <div v-else class="flex items-center justify-center [writing-mode:vertical-rl]">
            <slot name="sidebar-collapsed" />
          </div>
        </div>
      </div>

      <div v-if="$slots.content" data-column data-view="reading" :class="readingVariants.content({ expanded: contentExpanded, hasAside: Boolean($slots.aside), licensed: licensedContent })">
        <TableOfContentCollapseButton
          v-if="!isLoading && context === 'law'"
          v-model:expanded="contentExpanded"
          direction="left"
          :expanded-text="`Lukk ${readingTextLabelLower}`"
          :collapsed-text="`Åpne ${readingTextLabelLower}`"
          class="absolute top-[1.13rem] right-0 z-10"
          data-content-toggle
        />
        <div v-if="contentExpanded" ref="contentContainer" data-container tabindex="0" class="scrollbar-carbon overflow-y-auto [scrollbar-gutter:stable] py-m pl-m pr-[calc(theme(spacing.m)-var(--scrollbar-width))]">
          <!-- Masks the container's top padding so sticky reading headers don't reveal body text scrolling through the gap above them (top:0 pins below the padding box). -->
          <div aria-hidden="true" class="[position:sticky] top-0 z-10 h-0">
            <div class="absolute bottom-full inset-x-0 bg-surface-elevated" :class="{ 'h-m': loggedIn }" />
          </div>
          <slot name="content" :is-scroll-top="arrivedState.top" />
        </div>
        <div v-else class="flex items-center justify-center [writing-mode:vertical-rl]">
          <slot name="content-collapsed" />
        </div>
      </div>

      <div v-if="$slots.aside" data-column data-view="commentary" :class="readingVariants.aside({ licensed: licensedAside, expanded: commentExpanded, width: asideWidth, hasReference: Boolean($slots.reference) })">
        <TableOfContentCollapseButton
          v-if="displayControlButton"
          v-model:expanded="commentExpanded"
          direction="left"
          expanded-text="Minimer vindu"
          collapsed-text="Utvid vindu"
          class="absolute top-[1.13rem] right-0 z-10"
          data-aside-toggle
        />
        <div v-if="commentExpanded" ref="asideContainer" data-container tabindex="-1" class="scrollbar-carbon overflow-y-auto [scrollbar-gutter:stable] flex flex-col gap-m pr-[calc(theme(spacing.m)-var(--scrollbar-width))]">
          <slot name="aside" />
        </div>
        <div v-else class="flex items-center justify-center [writing-mode:vertical-rl]">
          <slot name="aside-collapsed" />
        </div>
      </div>

      <div v-if="$slots.reference" data-column data-view="reference" :class="readingVariants.reference({ open: Boolean($slots.reference) })">
        <div ref="referenceContainer" data-container tabindex="-1" class="px-m">
          <slot name="reference" />
        </div>
      </div>

      <SelectionTooltip />
      <MarkingsNoteTooltip />
    </div>
  </div>
</template>

<style scoped lang="postcss">
[data-column] {
  min-height: v-bind(columnHeight);
  max-height: v-bind(columnHeight);
}

[data-container] {
  /* Enable browser scroll anchoring to maintain scroll position when content is prepended */
  overflow-anchor: auto;
}

[data-transitions-off] [data-column] {
  transition: none !important;
}

@media (min-width: 768px) and (max-width: 1349px) {
  /* Tablet: show one right-column view at a time. The sidebar remains visible. */
  [data-reading-tablet][data-active-view='reading'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='reading'] [data-view='reference'],
  [data-reading-tablet][data-active-view='toc'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='toc'] [data-view='reference'],
  [data-reading-tablet][data-active-view='commentary'] [data-view='reading'],
  [data-reading-tablet][data-active-view='commentary'] [data-view='reference'],
  [data-reading-tablet][data-active-view='curated'] [data-view='reading'],
  [data-reading-tablet][data-active-view='curated'] [data-view='reference'],
  [data-reading-tablet][data-active-view='literature'] [data-view='reading'],
  [data-reading-tablet][data-active-view='literature'] [data-view='reference'],
  [data-reading-tablet][data-active-view='literature-list'] [data-view='reading'],
  [data-reading-tablet][data-active-view='literature-list'] [data-view='reference'],
  [data-reading-tablet][data-active-view='reference'] [data-view='reading'],
  [data-reading-tablet][data-active-view='reference'] [data-view='commentary'] {
    @apply hidden;
  }

  [data-reading-tablet][data-active-view='commentary'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='curated'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='literature'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='literature-list'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='reference'] [data-view='reference'] {
    @apply flex flex-1 w-auto max-w-none opacity-100;
  }

  /* Article references open beside the text, so collapse the sidebar to make room. The text card adjoins the rail and the Kilder card on both sides — no rounding */
  [data-reading-tablet][data-context='academic_article'][data-active-view='reference'] [data-view='reading'] {
    @apply flex rounded-none;
  }

  [data-reading-tablet][data-context='academic_article'][data-active-view='reference'] [data-view='commentary'] {
    @apply flex opacity-100 rounded-r-2xl;
  }

  [data-reading-tablet][data-active-view='reading'] [data-view='reading'],
  [data-reading-tablet][data-active-view='toc'] [data-view='reading'],
  [data-reading-tablet][data-active-view='curated'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='literature'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='literature-list'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='reference'] [data-view='reference'] {
    @apply rounded-r-2xl;
  }

  [data-reading-tablet][data-active-view='commentary'] [data-view='commentary'] {
    @apply rounded-r-2xl;
  }

  [data-reading-tablet][data-context='law'] [data-content-toggle],
  [data-reading-tablet][data-context='law'] [data-aside-toggle] {
    @apply hidden;
  }

  [data-reading-tablet][data-context='law'] [data-sidebar-expanded] {
    @apply w-[300px] max-w-[300px];
  }
}

@media (max-width: 767px) {
  /* Mobile: show one view at a time, use svh to avoid iOS Safari viewport height issues. */
  [data-reading-tablet] {
    /* Zero-width wrappers of hidden columns still count as flex items — the gap would offset the visible column */
    @apply gap-0;
  }

  [data-reading-tablet] [data-column] {
    @apply hidden w-full max-w-none rounded-none;
    min-height: v-bind(mobileColumnHeight);
    max-height: v-bind(mobileColumnHeight);
  }

  [data-reading-tablet][data-active-view='reading'] [data-view='reading'],
  [data-reading-tablet][data-active-view='toc'] [data-view='toc'],
  [data-reading-tablet][data-active-view='commentary'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='curated'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='literature'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='literature-list'] [data-view='commentary'],
  [data-reading-tablet][data-active-view='reference'] [data-view='reference'] {
    @apply flex flex-1 rounded-t-4xl;
  }

  [data-reading-tablet][data-context='academic_article'][data-active-view='reference'] [data-view='commentary'] {
    @apply flex flex-1 rounded-t-4xl;
  }

  /* Keep the TOC wrapper full width so it does not briefly collapse while search loads */
  [data-reading-tablet][data-active-view='toc'] [data-sidebar-wrapper] {
    @apply flex-1 min-w-0;
  }

  /* Stop iOS from dragging the whole page when this card reaches scroll end. */
  [data-reading-tablet] [data-container],
  [data-reading-tablet] [data-view='toc'] > .overflow-y-auto {
    overscroll-behavior: contain;
  }

  /* Mobile reading card gets 16px padding, Desktop uses 24px */
  [data-reading-tablet] [data-view='reading'] [data-container] {
    @apply pl-s pr-[calc(theme(spacing.s)-var(--scrollbar-width))];
  }

  [data-reading-tablet] [data-view='reference'] [data-container] {
    @apply px-s;
  }

  [data-reading-tablet] [data-view='commentary'] {
    @apply pl-s;
  }

  [data-reading-tablet] [data-view='commentary'] > [data-container] {
    @apply pr-[calc(theme(spacing.s)-var(--scrollbar-width))];
  }

  [data-reading-tablet] [data-view='toc'][data-sidebar-expanded] {
    @apply pl-s;
  }

  [data-reading-tablet] [data-view='toc'] > .overflow-y-auto {
    @apply pr-[calc(theme(spacing.s)-var(--scrollbar-width))];
  }

  [data-reading-tablet] [data-sidebar-toggle],
  [data-reading-tablet] [data-content-toggle],
  [data-reading-tablet] [data-aside-toggle] {
    @apply hidden;
  }
}

/* Slide the new view in on switch. Skip initial load and reduced motion. */
@media (max-width: 767px) and (prefers-reduced-motion: no-preference) {
  [data-reading-tablet][data-views-ready][data-active-view='reading'] [data-view='reading'],
  [data-reading-tablet][data-views-ready][data-active-view='toc'] [data-view='toc'],
  [data-reading-tablet][data-views-ready][data-active-view='commentary'] [data-view='commentary'],
  [data-reading-tablet][data-views-ready][data-active-view='curated'] [data-view='commentary'],
  [data-reading-tablet][data-views-ready][data-active-view='literature'] [data-view='commentary'],
  /* NB: the space before [data-view] is a descendant combinator — data-active-view sits on the wrapper, data-view on the column */
  [data-reading-tablet][data-views-ready][data-context='academic_article'][data-active-view='reference'] [data-view='commentary'],
  [data-reading-tablet][data-views-ready][data-active-view='reference'] [data-view='reference'] {
    animation: reading-view-slide-in 0.3s ease-out;
  }
}

@keyframes reading-view-slide-in {
  from {
    transform: translateX(1.5rem);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
